/**
 * TokenSidePanel
 *
 * A reusable right-side panel that displays grouped merge tokens for insertion
 * into a rich text editor. Used by both the Grouped Row Description dialog
 * and the Terms & Conditions dialog.
 *
 * Each token shows:
 *   - A friendly label (bold)
 *   - A short description of what it resolves to (muted, 1 line)
 *
 * Clicking a token calls `onInsert(token)` — the parent is responsible for
 * routing that to the correct editor (via ref or state).
 */

import * as React from 'react';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Braces, Lock } from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

export interface TokenItem {
  /** The raw token string, e.g. `{client.name}` or `{person}` */
  token: string;
  /** Friendly display label, e.g. "Client Name" */
  label: string;
  /** One-line human description of what this field resolves to */
  description: string;
  /** When true, the token is shown but greyed out (not available in this context) */
  dimmed?: boolean;
}

export interface TokenGroup {
  /** Group heading, e.g. "Client" or "Contextual" */
  label: string;
  /** Optional muted sub-description shown under the group heading */
  description?: string;
  /** Colour accent for the group heading pill */
  accent?: 'primary' | 'muted' | 'warning';
  tokens: TokenItem[];
}

export interface TokenSidePanelProps {
  groups: TokenGroup[];
  /** Called when a token is clicked */
  onInsert: (token: string) => void;
  /** Additional className for the outer container */
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

export function TokenSidePanel({ groups, onInsert, className }: TokenSidePanelProps) {
  const nonEmpty = groups.filter((g) => g.tokens.length > 0);

  return (
    <div
      className={cn(
        'flex flex-col w-56 shrink-0 border-l border-border',
        'bg-gradient-to-b from-accent/30 to-accent/10',
        className,
      )}
    >
      {/* Panel header */}
      <div className="px-3 pt-3.5 pb-3 border-b border-border bg-primary/8 shrink-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <div className="flex items-center justify-center w-5 h-5 rounded bg-primary/15 text-primary shrink-0">
            <Braces className="w-3 h-3" />
          </div>
          <p className="text-xs font-semibold text-primary tracking-wide">
            Insert Field
          </p>
        </div>
        <p className="text-[10.5px] text-muted-foreground leading-snug pl-6">
          Click a field to insert it at the cursor
        </p>
      </div>

      {/* Scrollable token list */}
      <ScrollArea className="flex-1">
        <div className="px-2 py-2.5 space-y-3.5">
          {nonEmpty.map((group) => (
            <div key={group.label}>
              {/* Group heading */}
              <div className="flex items-center gap-1.5 px-1 mb-1.5">
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
                    group.accent === 'warning'
                      ? 'bg-muted text-muted-foreground'
                      : group.accent === 'muted'
                      ? 'bg-muted text-muted-foreground'
                      : 'bg-primary/12 text-primary',
                  )}
                >
                  {group.label}
                </span>
              </div>
              {group.description && (
                <p className="text-[10px] text-muted-foreground/70 leading-tight px-2 mb-1.5">
                  {group.description}
                </p>
              )}

              {/* Tokens */}
              <div className="space-y-0.5">
                {group.tokens.map((item) => (
                  <button
                    key={item.token}
                    type="button"
                    disabled={item.dimmed}
                    onClick={() => !item.dimmed && onInsert(item.token)}
                    className={cn(
                      'w-full text-left rounded-lg px-2.5 py-2 transition-all duration-150 group border',
                      item.dimmed
                        ? 'opacity-40 cursor-not-allowed border-transparent'
                        : 'border-transparent hover:border-primary/20 hover:bg-primary/8 hover:shadow-xs cursor-pointer',
                    )}
                  >
                    <div className="flex items-start gap-1.5">
                      {item.dimmed ? (
                        <Lock className="w-2.5 h-2.5 mt-0.5 shrink-0 text-muted-foreground/50" />
                      ) : (
                        <span className="w-1.5 h-1.5 rounded-full bg-primary/40 mt-1.5 shrink-0 group-hover:bg-primary transition-colors" />
                      )}
                      <div className="min-w-0">
                        <p
                          className={cn(
                            'text-xs font-semibold leading-tight truncate',
                            item.dimmed
                              ? 'text-muted-foreground'
                              : 'text-foreground group-hover:text-primary transition-colors',
                          )}
                        >
                          {item.label}
                        </p>
                        {item.description && (
                          <p className="text-[10px] text-muted-foreground leading-snug mt-0.5">
                            {item.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}

          {nonEmpty.length === 0 && (
            <p className="text-xs text-muted-foreground px-1 py-2">
              No fields available.
            </p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
